import React from "react";
import { DomainPostItemType } from "pages/api/fetchDomainPosts";
import { PageBuilderBlogPost } from "@page-builder/ts/types";

export type ResponsiveImage = {
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
    "2xl"?: string;
};

export const ResponsiveImageRenderer = ({
    image,
    alt = "image",
}: {
    image: string | ResponsiveImage;
    alt?: string;
}) => {
    // If image is a string, render simple img
    if (typeof image === "string") {
        return <img src={image} alt={alt} loading="lazy" />;
    }

    // If image is an object with breakpoints
    const { sm, md, lg, xl, "2xl": xxl } = image;
    const defaultSrc = sm || md || lg || xl || xxl || "";

    // Build unique sources (skip if same as previous smaller breakpoint)
    const sources: Array<{ minWidth: number; src: string }> = [];

    if (xxl && xxl !== xl) sources.push({ minWidth: 1536, src: xxl });
    if (xl && xl !== lg) sources.push({ minWidth: 1280, src: xl });
    if (lg && lg !== md) sources.push({ minWidth: 1024, src: lg });
    if (md && md !== sm) sources.push({ minWidth: 768, src: md });
    if (sm) sources.push({ minWidth: 640, src: sm });

    return (
        <picture>
            {sources.map(({ minWidth, src }) => (
                <source
                    key={minWidth}
                    media={`(min-width: ${minWidth}px)`}
                    srcSet={src}
                />
            ))}
            <img src={defaultSrc} alt={alt} loading="lazy" />
        </picture>
    );
};

export function getPublishedDateLabel(
    post: DomainPostItemType | PageBuilderBlogPost,
): string {
    const now = new Date();
    const createdAt = post?.createdAt ? new Date(post.createdAt) : null;
    const updatedAt = post?.updatedAt ? new Date(post.updatedAt) : null;
    const publishedAt = post?.publishedAt ? new Date(post.publishedAt) : null;

    // Ignore publishedAt if it's in the future (≥ now + 1s)
    const validPublishedAt =
        publishedAt && publishedAt.getTime() < now.getTime() + 1000
            ? publishedAt
            : null;

    // Case 1: publishedAt exists and is valid, and is the most recent
    if (validPublishedAt && (!updatedAt || validPublishedAt >= updatedAt)) {
        return `Published On: ${validPublishedAt.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })}`;
    }

    // Case 2: updatedAt exists and is more recent
    if (updatedAt) {
        return `Updated On: ${updatedAt.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })}`;
    }

    // Case 3: fallback — no updatedAt and no valid publishedAt → use createdAt as Published
    if (createdAt) {
        return `Published On: ${createdAt.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })}`;
    }

    return "";
}

export function getPostReadingTime(
    post: PageBuilderBlogPost | null | undefined,
): string {
    if (!post?.taxonomies) return "";

    for (const taxonomy of post.taxonomies) {
        const field = taxonomy?.fields?.find(
            (f) => f.codeName === "postReadingTime",
        );
        if (field?.value) {
            return Number(field.value) > 1
                ? `${field.value} mins read`
                : `${field.value} min read`;
        }
    }

    return "";
}

/**
 * Finds a custom field by its code name within the post's taxonomies and
 * returns its value parsed as JSON when possible, otherwise the raw string.
 */

type ReturnedValue =
    | string
    | { label?: string }
    | undefined
    | { label?: string }[]
    | { title?: string; description?: string }[];

export function getPostFieldValue(
    post: DomainPostItemType,
    codeName: string,
): ReturnedValue {
    const raw = post?.taxonomyFieldGroups
        ?.flatMap((taxonomy) => taxonomy?.customFields ?? [])
        ?.find(
            (fieldValue) => fieldValue?.fieldCodeName === codeName,
        )?.fieldValue;

    if (raw == null) return undefined;

    try {
        return JSON.parse(raw) as ReturnedValue;
    } catch {
        return raw;
    }
}
